/**
 * MandantOverview — Read-Only-Vorschau aller Mandanten-Eingaben.
 *
 * Wird vom Mandanten ans Ende seines Wizard-Flows aufgerufen, um:
 *   - alle Eingaben kompakt zu prüfen
 *   - per Pflicht-Checkbox zu bestätigen, dass die Angaben korrekt sind
 *   - dem Berater per Klick zu signalisieren „Ich bin fertig"
 *
 * Übergabe-Persistenz (Phase A, ohne Backend-Change):
 *   Wir setzen einen Marker `mandant.handover.completed.{caseId}` =
 *   ISO-Timestamp im localStorage. In Phase B (mit case_documents +
 *   case_clarifications) wird das auf eine echte DB-Spalte (z.B.
 *   `cases.mandant_completed_at`) umgestellt. Bis dahin bleibt der Berater
 *   informiert über das Status-Board (Phase B-Integrator-Agent baut den
 *   Read-Pfad ein).
 *
 * Werte-Formatierung: Euro mit deutschem Locale (de-DE), Datum dd.mm.yyyy,
 * Stunden als Integer/Decimal mit Komma. Leere/null Werte werden als
 * „— noch nicht ausgefüllt" angezeigt.
 *
 * Layout: Editorial-Stil konsistent zu MandantCaseHome — schmaler max-w-3xl
 * Container, Source-Serif-Headlines, Pastell-Akzente pro Modul-Card.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, CheckCircle2, ArrowRight, IdCard, FileText, Printer } from 'lucide-react';
import { de } from '../lib/i18n/de';
import { useWizardStore, selectStepStatus, selectCompletedCount } from '../lib/store/wizard';
import type { WizardCaseState, ResourcesDraft } from '../lib/store/wizard';
import { presentDimensionFields } from '../lib/store/dimensionFieldPresenter';
import { STEPS, TOTAL_STEPS, type StepKey } from '../components/hub/dimensions';
import { Button } from '../components/ui/Button';
import { useAuthOrSession } from '../lib/authOrSession';
import { getCase } from '../lib/supabaseCases';
import { DOCUMENT_TYPES, listCaseDocuments, type CaseDocument } from '../lib/supabaseDocuments';
import {
  caseToDraft,
  validateStammdaten,
  type StammdatenDraft,
} from '../lib/stammdaten/stammdatenValidation';

const HANDOVER_STORAGE_PREFIX = 'mandant.handover.completed.';

export function MandantOverview() {
  const { caseId = '' } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const ensureCase = useWizardStore((s) => s.ensureCase);
  const caseState = useWizardStore((s) => s.cases[caseId]);

  useEffect(() => {
    ensureCase(caseId);
  }, [caseId, ensureCase]);

  const [confirmed, setConfirmed] = useState(false);
  const [handoverAt, setHandoverAt] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      return window.localStorage.getItem(HANDOVER_STORAGE_PREFIX + caseId);
    } catch {
      return null;
    }
  });

  const completedCount = selectCompletedCount(caseState);
  const completionRatio = completedCount / TOTAL_STEPS;

  // Triage ist die einzige Pflicht-Voraussetzung (Konzept §4.1) — ohne sie
  // hat die Stellungnahme keinen rechtlichen Anker.
  const missingSteps: ReadonlyArray<StepKey> = useMemo(() => {
    if (!caseState) return [];
    const missing: StepKey[] = [];
    for (const s of STEPS) {
      const status = selectStepStatus(caseState, s.id);
      if (status !== 'complete') missing.push(s.id);
    }
    return missing;
  }, [caseState]);

  const triageOpen = missingSteps.includes('triage');
  // Wir lassen die Übergabe immer zu, sofern Triage steht UND der Mandant
  // explizit per Checkbox bestätigt hat. Andere Module sind „dringend
  // empfohlen", aber kein Lock.
  const canHandOver = !triageOpen && confirmed;

  const handleHandOver = () => {
    if (!canHandOver) return;
    const iso = new Date().toISOString();
    try {
      window.localStorage.setItem(HANDOVER_STORAGE_PREFIX + caseId, iso);
    } catch {
      // siehe MandantOverview-Header — Persistenz best-effort
    }
    setHandoverAt(iso);
    setConfirmed(false);
  };

  return (
    <article className="pb-24 pt-8 md:pt-12">
      <Link
        to={`/mandant/case/${caseId}`}
        className="inline-flex items-center gap-1.5 font-sans text-xs font-medium uppercase tracking-widest text-stone-500 hover:text-stone-900 hover:underline"
      >
        <ChevronLeft size={14} aria-hidden="true" />
        {de.mandant.overview.backLink}
      </Link>

      <Hero completedCount={completedCount} ratio={completionRatio} />

      {/* Stammdaten-Übersicht */}
      <StammdatenSection caseId={caseId} />

      {/* Dokumenten-Übersicht */}
      <DocumentsSection caseId={caseId} />

      {/* Modul-Liste mit Read-Only-Werten */}
      <section className="mt-10 space-y-6">
        {caseState ? (
          <>
            <ModuleCard caseId={caseId} stepId="triage" caseState={caseState} />
            <ModuleCard caseId={caseId} stepId="identity" caseState={caseState} />
            <ModuleCard caseId={caseId} stepId="activity" caseState={caseState} />
            <ModuleCard caseId={caseId} stepId="style" caseState={caseState} />
            <ModuleCard caseId={caseId} stepId="resources" caseState={caseState} />
            <ModuleCard caseId={caseId} stepId="health" caseState={caseState} />
            <ModuleCard caseId={caseId} stepId="private" caseState={caseState} />
          </>
        ) : (
          <p className="font-serif text-base italic text-stone-500">Mandat wird geladen …</p>
        )}
      </section>

      {/* Hinweis auf fehlende Module (NICHT-Pflicht) */}
      {missingSteps.length > 0 && missingSteps.length < TOTAL_STEPS && (
        <section className="mt-10 rounded-sm border border-amber-200 bg-amber-50 px-5 py-5">
          <h3 className="font-sans text-xs font-medium uppercase tracking-widest text-amber-900">
            {de.mandant.overview.missingTitle}
          </h3>
          <p className="mt-2 font-serif text-sm leading-relaxed text-amber-900">
            {de.mandant.overview.missingIntro}
          </p>
          <ul className="mt-3 space-y-1">
            {missingSteps.map((id) => {
              const step = STEPS.find((s) => s.id === id);
              if (!step) return null;
              return (
                <li key={id}>
                  <Link
                    to={`/mandant/case/${caseId}/wizard/${id}`}
                    className="font-sans text-xs font-medium uppercase tracking-widest text-amber-900 underline hover:text-amber-700"
                  >
                    Modul {step.nummer} · {step.name} →
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* Übergabe-Block */}
      <section className="mt-10 border-t border-stone-200 pt-8">
        {handoverAt ? (
          <HandoverConfirmedBlock handoverAt={handoverAt} onResubmit={handleHandOver} />
        ) : (
          <HandoverActionBlock
            confirmed={confirmed}
            onConfirmedChange={setConfirmed}
            canHandOver={canHandOver}
            triageOpen={triageOpen}
            onHandOver={handleHandOver}
          />
        )}
      </section>

      {/* Zurück-Button + Druckansicht am Footer */}
      <div className="mt-10 flex flex-col items-stretch gap-3 border-t border-stone-200 pt-6 md:flex-row md:items-center md:justify-between">
        <button
          type="button"
          onClick={() => {
            void navigate(`/mandant/case/${caseId}`);
          }}
          className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500 hover:text-stone-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
        >
          ← {de.mandant.overview.backLink}
        </button>
        <button
          type="button"
          onClick={() => {
            if (typeof window !== 'undefined') {
              window.open(
                `/mandant/case/${caseId}/print?autoprint=1`,
                '_blank',
                'noopener,noreferrer',
              );
            }
          }}
          className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-sm border border-stone-900 bg-white px-5 py-2 font-sans text-xs font-medium uppercase tracking-widest text-stone-900 hover:bg-stone-900 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
        >
          <Printer size={14} aria-hidden="true" />
          {de.print.printButton}
        </button>
      </div>
    </article>
  );
}

// -----------------------------------------------------------------------------
// Hero
// -----------------------------------------------------------------------------

function Hero({ completedCount, ratio }: { completedCount: number; ratio: number }) {
  const [line1, line2] = de.mandant.overview.title.split('\n');
  const pct = Math.round(ratio * 100);
  return (
    <header className="relative pb-6 pt-6 md:pb-10 md:pt-10">
      <div
        aria-hidden="true"
        className="absolute right-0 top-6 hidden h-24 w-24 rounded-sm md:block"
        style={{ backgroundColor: 'var(--color-dim-resources)', opacity: 0.18 }}
      />
      <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
        {de.mandant.overview.sectionLabel}
      </p>
      <h1 className="mt-3 font-serif text-4xl font-black leading-none tracking-tight text-stone-900 md:text-6xl">
        {line1}
        {line2 && (
          <>
            <br />
            {line2}
          </>
        )}
      </h1>
      <p className="mt-3 max-w-prose font-serif text-base italic text-stone-500 md:mt-4 md:text-lg">
        {de.mandant.overview.subtitle}
      </p>
      <p className="mt-4 font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
        {completedCount} / {TOTAL_STEPS} Module · {pct}%
      </p>
    </header>
  );
}

// -----------------------------------------------------------------------------
// Modul-Card
// -----------------------------------------------------------------------------

interface ModuleCardProps {
  readonly caseId: string;
  readonly stepId: StepKey;
  readonly caseState: WizardCaseState;
}

function ModuleCard({ caseId, stepId, caseState }: ModuleCardProps) {
  const step = STEPS.find((s) => s.id === stepId);
  if (!step) return null;
  const status = selectStepStatus(caseState, stepId);
  const statusLabel =
    status === 'complete'
      ? de.mandant.overview.moduleStatusComplete
      : status === 'in-progress'
        ? de.mandant.overview.moduleStatusInProgress
        : de.mandant.overview.moduleStatusEmpty;
  const statusCls =
    status === 'complete'
      ? 'bg-emerald-100 text-emerald-800'
      : status === 'in-progress'
        ? 'bg-amber-100 text-amber-800'
        : 'bg-stone-100 text-stone-500';

  return (
    <div className="rounded-sm border border-stone-200 bg-white">
      <header className="flex items-start justify-between gap-4 border-b border-stone-100 px-5 py-4 md:px-6 md:py-5">
        <div className="flex items-start gap-4 min-w-0 flex-1">
          <span
            aria-hidden="true"
            className="mt-1 inline-block h-10 w-10 flex-shrink-0 rounded-sm"
            style={{ backgroundColor: step.accentHex, opacity: 0.4 }}
          />
          <div className="min-w-0 flex-1">
            <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
              Modul {step.nummer}
            </p>
            <p className="mt-0.5 font-serif text-base font-bold tracking-tight text-stone-900">
              {step.name}
            </p>
          </div>
        </div>
        <span
          className={`inline-flex flex-shrink-0 items-center rounded-sm px-2 py-0.5 font-sans text-[10px] font-medium uppercase tracking-widest ${statusCls}`}
        >
          {statusLabel}
        </span>
      </header>

      <div className="px-5 py-4 md:px-6 md:py-5">
        <ModuleBody stepId={stepId} caseState={caseState} />
        <div className="mt-4 border-t border-stone-100 pt-3">
          <Link
            to={`/mandant/case/${caseId}/wizard/${stepId}`}
            className="inline-flex min-h-[36px] items-center gap-1 font-sans text-xs font-medium uppercase tracking-widest text-stone-700 hover:text-stone-900 hover:underline"
          >
            {de.mandant.overview.editLink} →
          </Link>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Modul-Body — pro StepKey eigene Detail-Anzeige
// -----------------------------------------------------------------------------

function ModuleBody({ stepId, caseState }: { stepId: StepKey; caseState: WizardCaseState }) {
  if (stepId === 'triage') {
    const value = caseState.triage.legalBasis;
    if (!value) return <NoValue />;
    return (
      <KvList>
        <Kv label="Rechtsgrundlage" value={mapLegalBasis(value)} />
      </KvList>
    );
  }

  if (stepId === 'resources') {
    return <ResourcesBody resources={caseState.resources} />;
  }

  // Die 5 Freitext-Dimensionen (identity, activity, style, health, private):
  // Werte werden über den zentralen Presenter menschenlesbar gemacht
  // (Label statt Roh-Schlüssel, Ja/Nein statt true/false, Listen aufgelöst).
  const dim = caseState.dimensionData[stepId];
  if (!dim) return <NoValue />;
  const presented = presentDimensionFields(stepId, dim.fields);
  if (presented.length === 0) return <NoValue />;

  return (
    <KvList>
      {presented.map((f, i) => (
        <Kv key={i} label={f.label} value={truncate(f.display, 240)} multiline />
      ))}
    </KvList>
  );
}

function ResourcesBody({ resources }: { resources: ResourcesDraft }) {
  const r = resources;
  const profit =
    r.revenueCents !== null && r.costsCents !== null ? r.revenueCents - r.costsCents : null;
  const kduTotal = r.kdu.kaltCents + r.kdu.nebenCents + r.kdu.heizCents;
  return (
    <KvList>
      <Kv label="Umsatz / Monat" value={formatEuro(r.revenueCents)} />
      <Kv label="Betriebsausgaben / Monat" value={formatEuro(r.costsCents)} />
      <Kv label="Daraus errechneter Gewinn" value={formatEuro(profit)} />
      <Kv label="Abrechenbare Stunden / Monat" value={formatHours(r.hoursPerMonth)} />
      <Kv label="Liquide Mittel" value={formatEuro(r.liquidityCents)} />
      <Kv
        label="Bedarfsgemeinschaft"
        value={
          r.bgComposition.length > 0
            ? `${r.bgComposition.length.toString()} Personen (${r.bgComposition.map((m) => mapBgRole(m.role)).join(', ')})`
            : null
        }
      />
      <Kv
        label="Kosten der Unterkunft (gesamt)"
        value={kduTotal > 0 ? formatEuro(kduTotal) : null}
      />
    </KvList>
  );
}

// -----------------------------------------------------------------------------
// Übergabe-Blöcke
// -----------------------------------------------------------------------------

function HandoverActionBlock({
  confirmed,
  onConfirmedChange,
  canHandOver,
  triageOpen,
  onHandOver,
}: {
  confirmed: boolean;
  onConfirmedChange: (v: boolean) => void;
  canHandOver: boolean;
  triageOpen: boolean;
  onHandOver: () => void;
}) {
  return (
    <div>
      <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
        Übergabe
      </p>
      <h2 className="mt-2 font-serif text-2xl font-bold tracking-tight text-stone-900 md:text-3xl">
        AN BERATER ÜBERGEBEN.
      </h2>

      <label className="mt-5 flex items-start gap-3 rounded-sm border border-stone-200 bg-stone-50 px-4 py-4 cursor-pointer hover:bg-stone-100">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => {
            onConfirmedChange(e.target.checked);
          }}
          className="mt-1 h-5 w-5 flex-shrink-0 cursor-pointer rounded-sm border-stone-400 text-stone-900 focus:ring-2 focus:ring-stone-900 focus:ring-offset-2"
        />
        <span className="font-serif text-sm leading-relaxed text-stone-800">
          {de.mandant.overview.confirmCheckbox}
        </span>
      </label>

      {triageOpen && (
        <p className="mt-3 font-serif text-sm italic text-rose-700">
          Bitte schließen Sie zuerst die Triage ab — sie ist Pflicht.
        </p>
      )}

      <div className="mt-5 flex flex-col items-stretch gap-3 md:flex-row md:items-center md:justify-between">
        <p className="font-serif text-xs italic text-stone-500 md:max-w-md">
          {!canHandOver && !triageOpen ? de.mandant.overview.handoverDisabledHint : ''}
        </p>
        <Button type="button" onClick={onHandOver} disabled={!canHandOver}>
          {de.mandant.overview.handoverButton}
          <ArrowRight size={14} aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}

function HandoverConfirmedBlock({
  handoverAt,
  onResubmit,
}: {
  handoverAt: string;
  onResubmit: () => void;
}) {
  const niceDate = formatDateTime(handoverAt);
  return (
    <div>
      <p className="font-sans text-xs font-medium uppercase tracking-widest text-emerald-700">
        Übergabe
      </p>
      <h2 className="mt-2 flex items-baseline gap-3 font-serif text-2xl font-bold tracking-tight text-stone-900 md:text-3xl">
        <CheckCircle2 size={20} className="text-emerald-600" aria-hidden="true" />
        {de.mandant.overview.handoverSuccessTitle}
      </h2>
      <p className="mt-3 max-w-prose font-serif text-sm leading-relaxed text-stone-600">
        {de.mandant.overview.handoverSuccessBody}
      </p>
      <p className="mt-3 font-serif text-xs italic text-stone-500">
        {de.mandant.overview.handoverAlreadyDone.replace('{date}', niceDate)}
      </p>
      <button
        type="button"
        onClick={onResubmit}
        className="mt-4 inline-flex min-h-[40px] items-center justify-center rounded-sm border border-stone-200 bg-white px-5 py-2 font-sans text-xs font-medium uppercase tracking-widest text-stone-700 hover:bg-stone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
      >
        {de.mandant.overview.handoverAgain}
      </button>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Kleine UI-Bausteine
// -----------------------------------------------------------------------------

function KvList({ children }: { children: React.ReactNode }) {
  return <dl className="space-y-2">{children}</dl>;
}

function Kv({
  label,
  value,
  multiline = false,
}: {
  label: string;
  value: string | null;
  multiline?: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5 md:flex-row md:items-baseline md:gap-4">
      <dt className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500 md:w-56 md:flex-shrink-0">
        {label}
      </dt>
      <dd
        className={`font-serif text-sm text-stone-900 ${multiline ? 'whitespace-pre-line' : 'tabular-nums'}`}
      >
        {value ?? <span className="italic text-stone-500">{de.mandant.overview.noValue}</span>}
      </dd>
    </div>
  );
}

function NoValue() {
  return <p className="font-serif text-sm italic text-stone-500">{de.mandant.overview.noValue}</p>;
}

// -----------------------------------------------------------------------------
// Formatter (pure)
// -----------------------------------------------------------------------------

export function formatEuro(cents: number | null): string | null {
  if (cents === null) return null;
  const euros = cents / 100;
  return `${euros.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

export function formatHours(h: number | null): string | null {
  if (h === null) return null;
  if (Number.isInteger(h)) return `${h.toString()} h / Monat`;
  return `${h.toLocaleString('de-DE', { maximumFractionDigits: 1 })} h / Monat`;
}

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function truncate(s: string, maxLen: number): string {
  if (s.length <= maxLen) return s;
  return `${s.slice(0, maxLen - 1).trimEnd()}…`;
}

function mapBgRole(role: string): string {
  switch (role) {
    case 'antragsteller':
      return 'Antragsteller:in';
    case 'partner':
      return 'Partner:in';
    case 'kind':
      return 'Kind';
    default:
      return role;
  }
}

function mapLegalBasis(value: string): string {
  // Wir verwenden die Beschriftungen aus i18n direkt, statt sie zu duplizieren.
  const opts = de.steps.triage.legalBasisOptions as Readonly<Record<string, string>>;
  return opts[value] ?? value;
}

// Hilfs-Export für Tests
export const HANDOVER_STORAGE_PREFIX_FOR_TESTS = HANDOVER_STORAGE_PREFIX;

// -----------------------------------------------------------------------------
// Phase A: Stammdaten + Dokumente in der Übersicht
// -----------------------------------------------------------------------------

function StammdatenSection({ caseId }: { caseId: string }) {
  const { isMock } = useAuthOrSession();
  const [draft, setDraft] = useState<StammdatenDraft | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (isMock) {
      setDraft(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const c = await getCase(caseId);
      if (!c) {
        setDraft(null);
      } else {
        setDraft(caseToDraft(c));
      }
    } catch (err) {
      console.warn('StammdatenSection: getCase failed', err);
      setDraft(null);
    } finally {
      setLoading(false);
    }
  }, [caseId, isMock]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (loading) return null;

  const validation = draft ? validateStammdaten(draft) : null;
  const hasAny =
    draft !== null &&
    (draft.clientFullName.length > 0 ||
      draft.businessBranch.length > 0 ||
      draft.businessDescription.length > 0);

  return (
    <section className="mt-10">
      <header className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
            Modul 00
          </p>
          <h2 className="mt-1 inline-flex items-baseline gap-2 font-serif text-2xl font-bold tracking-tight text-stone-900">
            <IdCard size={20} className="text-stone-500" />
            {de.mandant.overview.stammdatenSectionTitle}
          </h2>
        </div>
        <Link
          to={`/mandant/case/${caseId}/wizard/stammdaten`}
          className="inline-flex min-h-[36px] items-center font-sans text-xs font-medium uppercase tracking-widest text-stone-700 underline-offset-2 hover:text-stone-900 hover:underline"
        >
          {hasAny ? de.mandant.overview.stammdatenChangeLink : de.mandant.overview.stammdatenGoFill}
        </Link>
      </header>

      {!hasAny ? (
        <p className="mt-3 font-serif text-sm italic text-stone-500">
          {de.mandant.overview.stammdatenEmpty}
        </p>
      ) : (
        <div className="mt-4 rounded-sm border border-stone-200 bg-white px-5 py-4 md:px-6">
          <KvList>
            <Kv label="Name" value={draft?.clientFullName || null} />
            <Kv label="Geburtsdatum" value={formatDateIso(draft?.clientBirthdate)} />
            <Kv label="Anschrift" value={draft?.clientAddress || null} multiline />
            <Kv label="Branche" value={draft?.businessBranch || null} />
            <Kv label="Rechtsform" value={draft?.businessLegalForm || null} />
            <Kv
              label="Beginn der Selbständigkeit"
              value={formatDateIso(draft?.businessStartDate)}
            />
            <Kv label="Steuernummer" value={draft?.businessTaxId || null} />
            <Kv label="Kurzbeschreibung" value={draft?.businessDescription || null} multiline />
          </KvList>
          {validation && !validation.isComplete && (
            <p className="mt-3 font-serif text-xs italic text-amber-700">
              Noch unvollständig: {validation.missingFields.length} Pflichtfeld(er) offen.
            </p>
          )}
        </div>
      )}
    </section>
  );
}

function DocumentsSection({ caseId }: { caseId: string }) {
  const { isMock } = useAuthOrSession();
  const [docs, setDocs] = useState<ReadonlyArray<CaseDocument>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (isMock) {
      setDocs([]);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }
    void (async () => {
      setLoading(true);
      try {
        const list = await listCaseDocuments(caseId, { onlyActive: true });
        if (!cancelled) setDocs(list);
      } catch (err) {
        console.warn('DocumentsSection: listCaseDocuments failed', err);
        if (!cancelled) setDocs([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [caseId, isMock]);

  if (loading) return null;

  // Group by docType
  const groups: Array<{ type: string; label: string; items: CaseDocument[] }> = Object.entries(
    DOCUMENT_TYPES,
  ).map(([t, meta]) => ({
    type: t,
    label: meta.label,
    items: docs.filter((d) => d.doc_type === t).slice(),
  }));
  const groupsWithDocs = groups.filter((g) => g.items.length > 0);

  return (
    <section className="mt-8">
      <header className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
            Anhang
          </p>
          <h2 className="mt-1 inline-flex items-baseline gap-2 font-serif text-2xl font-bold tracking-tight text-stone-900">
            <FileText size={20} className="text-stone-500" />
            {de.mandant.overview.docsSectionTitle}
          </h2>
        </div>
        <Link
          to={`/mandant/case/${caseId}`}
          className="inline-flex min-h-[36px] items-center font-sans text-xs font-medium uppercase tracking-widest text-stone-700 underline-offset-2 hover:text-stone-900 hover:underline"
        >
          {groupsWithDocs.length > 0
            ? de.mandant.overview.docsChangeLink
            : de.mandant.overview.docsGoUpload}
        </Link>
      </header>

      {groupsWithDocs.length === 0 ? (
        <p className="mt-3 font-serif text-sm italic text-stone-500">
          {de.mandant.overview.docsEmpty}
        </p>
      ) : (
        <div className="mt-4 rounded-sm border border-stone-200 bg-white px-5 py-4 md:px-6">
          <ul className="divide-y divide-stone-100">
            {groupsWithDocs.map((g) => (
              <li key={g.type} className="py-3">
                <p className="font-sans text-[11px] font-medium uppercase tracking-widest text-stone-500">
                  {g.label}
                </p>
                <ul className="mt-1 space-y-0.5">
                  {g.items.map((d) => (
                    <li key={d.id} className="flex items-baseline justify-between gap-3">
                      <p className="truncate font-serif text-sm text-stone-900">
                        {d.original_filename}
                      </p>
                      <p className="flex-shrink-0 font-sans text-[10px] uppercase tracking-widest text-stone-500">
                        {new Date(d.uploaded_at).toLocaleDateString('de-DE')}
                      </p>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function formatDateIso(iso: string | null | undefined): string | null {
  if (!iso || iso.length === 0) return null;
  try {
    return new Date(`${iso}T00:00:00Z`).toLocaleDateString('de-DE');
  } catch {
    return iso;
  }
}
